import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Wordmark from "@/components/Wordmark";
import {
  LayoutDashboard, Radio, Users, Target, TrendingUp, CalendarClock,
  BarChart3, Footprints, QrCode, UserCog, FileText, Settings as SettingsIcon,
  LogOut, Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/live", label: "Live Visitors", icon: Radio },
  { to: "/admin/visitors", label: "Visitor Log", icon: Users },
  { to: "/admin/leads", label: "Leads", icon: Target },
  { to: "/admin/conversions", label: "Conversions", icon: TrendingUp },
  { to: "/admin/followups", label: "Follow-ups", icon: CalendarClock },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/walking", label: "Walking Analytics", icon: Footprints },
  { to: "/admin/campaigns", label: "QR & Campaigns", icon: QrCode },
  { to: "/admin/team", label: "Team Performance", icon: UserCog },
  { to: "/admin/reports", label: "Reports", icon: FileText },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const sidebar = (
    <div className="h-full flex flex-col" style={{ background: "#081226" }}>
      <div className="px-5 py-6 border-b border-white/5"><Wordmark size="sm" /></div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
            data-testid={`nav-${label.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive ? "bg-[#E8752A] text-[#081226]" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`}>
            <Icon size={18} /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="flex items-center gap-3 px-2 py-2">
          {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full" /> :
            <div className="w-9 h-9 rounded-full bg-[#E8752A] text-[#081226] flex items-center justify-center font-bold">{user?.name?.[0]}</div>}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[#E8752A] text-[11px] font-bold">{user?.role}</p>
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="text-slate-400 hover:text-white p-1.5"><LogOut size={18} /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[260px] z-30">{sidebar}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-[260px]">{sidebar}</div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)}><Menu /></button>
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 live-dot" />
              <span className="font-semibold text-slate-700">Live</span>
              <span className="text-slate-400 font-mono-num ml-2">{clock.toLocaleTimeString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/register")} data-testid="new-walkin-btn"
              className="text-sm font-bold px-4 py-2 rounded-full text-white" style={{ background: "linear-gradient(90deg,#D95D1E,#C04A0E)" }}>
              + New Walk-in
            </button>
          </div>
        </header>
        <main className="p-5 sm:p-7 max-w-[1500px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
